/**
 * result-v2/adapters/summary.ts
 * ---------------------------------------------------------------------------
 * Everything the result HEADER and the PIVOT RAIL need, derived from the actual
 * payload rather than from the workflow type.
 *
 * Why this exists: the header used to print the raw criteria and the rail
 * offered a fixed guess per type — so a Name SocialScan that found e-mails,
 * phones and social IDs still only suggested "Face WebScan", and BreachScan
 * suggested nothing useful at all.
 *
 * `harvest()` walks every entity and collects the identifiers actually present.
 * Pivots are then built from what was found, so they are always actionable.
 */
import { cleanScalar } from './providers'
import { Source, Type } from '../../../store/searcher/reducer'

export type Pivot = {
  title: string
  sub: string
  credits: number
  colour: string
  /** workflow `type` to launch — matched against a real ILookup from the
   * `state.searcher.lookups` catalogue when the rail's button is clicked. */
  type: Type
  /** the identifier to search — empty when nothing concrete was harvested
   * (the two generic fallback pivots below). */
  value: string
  /** sub-type to pass as `startLookup`'s `lookupType` arg — only meaningful
   * for multi-source workflows like `deepweb` (BreachScan). */
  sourceKind?: Source
  /**
   * True when running this pivot needs something the rail can't supply on
   * its own — an uploaded image (Face WebScan) or a specific social
   * platform we can't disambiguate from the harvested id alone (BreachScan
   * · Social ID could be vkid/instagramid/facebookid/... — harvest() only
   * knows it looked like *a* social id, not which platform). These pivots
   * open the search console pre-filled instead of launching directly.
   */
  needsExtra?: boolean
}
export type Summary = {
  /** what to show as the result title — a resolved name beats a raw identifier */
  title: string
  /** "Israeli mobile · Active on 3 messengers · 1 e-mail found" */
  answer: [string, string][]
  pivots: Pivot[]
  /**
   * First photo URL harvested from anywhere in the payload (see `harvest()`'s
   * `IMG_KEYS` match), regardless of workflow type — used by ResultView.tsx's
   * header tile so a real picture replaces the initials/glyph placeholder
   * whenever the result actually has one, the same way `title` already
   * prefers a resolved name over the raw search criteria.
   */
  photo?: string
  /**
   * Every photo URL harvest() found, in the same order as `photo` (which is
   * just `photos[0]`) — ResultView.tsx's header tile needs the full list,
   * not just the first one, so it can fall back to the next candidate when
   * a URL turns out to be dead (expired signed CDN link, 404, ...) instead
   * of leaving a broken-image icon in place of the initials it would
   * otherwise have shown.
   */
  photos: string[]
}

const C = {
  phone: 'linear-gradient(135deg,#2D89F4,#5FB0FF)',
  email: 'linear-gradient(135deg,#7C5CFF,#A78BFA)',
  breach: 'linear-gradient(135deg,#EF4444,#F87171)',
  face: 'linear-gradient(135deg,#0EA5A5,#34D399)',
  social: 'linear-gradient(135deg,#F59E0B,#FBBF24)',
  ai: 'linear-gradient(135deg,#EC4899,#F472B6)',
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i
const PHONE_RE = /^\+?\d[\d\s()-]{7,}$/
const URL_RE = /^https?:\/\//i
const NAME_KEYS = /^(name|displayName|profileName|fullName|firstName|lastName|personName)$/i
const IMG_KEYS = /(image|photo|avatar|thumbnail|picture)/i
const SOCIAL_KEYS = /(vkid|instagramid|facebookid|linkedinid|telegramid|twitterid|profile_id|publicIdentifier|skype_id)/i

export type Harvest = {
  /** ordered by how many providers reported them, most corroborated first */
  names: string[]
  emails: string[]
  phones: string[]
  socialIds: string[]
  images: string[]
  messengers: string[]
  carrier?: string
  country?: string
  headline?: string
  breaches: string[]
}

const MESSENGER_KEYS = ['whatsapp', 'viber', 'telegram', 'getcontact', 'botim', 'imo', 'skype', 'teams']

/** Walk the whole payload once and collect every identifier worth pivoting on. */
export function harvest(apiData: any): Harvest {
  const out: Harvest = { names: [], emails: [], phones: [], socialIds: [], images: [], messengers: [], breaches: [] }
  const nameHits = new Map<string, number>()
  const push = (arr: string[], v: string) => {
    const c = cleanScalar(v)
    // Signed CDN image URLs (TikTok's tos-alisg-avt-*.tiktokcdn.com links
    // carry a token/signature/expiry query string) routinely run 300+
    // characters — the 200-char cap below exists to filter junk out of
    // names/emails/ids, not to reject a real photo URL, so images get a
    // much longer ceiling instead of the same one.
    const limit = arr === out.images ? 2000 : 200
    if (!c || c.length >= limit) return
    if (arr === out.names) nameHits.set(c, (nameHits.get(c) || 0) + 1)
    if (!arr.includes(c)) arr.push(c)
  }

  const entities: any[] = Array.isArray(apiData?.apiDataEntities) ? apiData.apiDataEntities : []

  entities.forEach((e) => {
    const from = String(e?.from ?? '').toLowerCase()
    const payload = e?.data && !Array.isArray(e.data) ? e.data[e.from] ?? e.data : e?.data

    // which messengers reported this identifier as registered
    if (MESSENGER_KEYS.includes(from) && payload && typeof payload === 'object') {
      const registered = payload.is_user === true || payload.isUser === true || !!payload.name || !!payload.avatar
      if (registered && !out.messengers.includes(from)) out.messengers.push(from)
    }

    // A `name` nested under a company, school or position is an ORGANISATION,
    // not the person — that is how "ESPY - OSINT Fraud Prevention" became the
    // title of an e-mail lookup.
    const ORG_PATH = /(compan|school|position|organi|employer|institution|education|group)/i

    const walk = (v: any, key = '', depth = 0, path = '') => {
      // Extra headroom for double-wrapped envelopes like irbis-social-tiktok's
      // ([entity.data] -> {data:[...]} -> row -> field), which already sits
      // right at the old depth>4 cutoff — a source nesting one level deeper
      // still (e.g. a field under row.profileData) would otherwise silently
      // stop being walked.
      if (v === null || v === undefined || depth > 6) return
      if (Array.isArray(v)) { v.slice(0, 30).forEach((x) => walk(x, key, depth + 1, path)); return }
      if (typeof v === 'object') {
        // compose a full name when first and last arrive as separate fields
        const first = v.firstName ?? v.first
        const last = v.lastName ?? v.last
        if (first && last && !ORG_PATH.test(path)) push(out.names, `${first} ${last}`)
        Object.entries(v).forEach(([k, val]) => walk(val, k, depth + 1, `${path}.${k}`))
        return
      }

      const s = String(v)
      if (EMAIL_RE.test(s)) { push(out.emails, s); return }
      // Checking `path` too (not just the immediate `key`) catches a value
      // sitting under an image-shaped ancestor whose own leaf key is generic
      // — e.g. combined_email's Glook profile photo at
      // `...profilePhotos.PROFILE.url`, where the leaf key is just "url" and
      // only the parent "profilePhotos" says what it actually is. Without
      // this, PersonTemplate's own Photos card (built by prepareCommonResult,
      // a separate and more source-specific pipeline) could show a real
      // photo while the header still fell back to initials.
      if ((IMG_KEYS.test(key) || IMG_KEYS.test(path)) && URL_RE.test(s)) { push(out.images, s); return }
      if (NAME_KEYS.test(key) && !ORG_PATH.test(path) && /[a-zA-Zа-яА-Я]{2,}/.test(s) && s.length < 60) { push(out.names, s); return }
      if (/^(e164Format|phone|phoneNumber|number)$/i.test(key) && PHONE_RE.test(s)) { push(out.phones, s); return }
      if (SOCIAL_KEYS.test(key) && s.length < 60) { push(out.socialIds, s); return }
      if (/^carrier$/i.test(key)) out.carrier = out.carrier || s
      if (/^(country|countryCode|isocode)$/i.test(key) && s.length <= 24) out.country = out.country || s
      if (/^(headline|title)$/i.test(key) && s.length < 120) out.headline = out.headline || s
      if (/^(Name|Title)$/.test(key) && /breach/i.test(String(from))) push(out.breaches, s)
    }
    walk(payload)
  })

  // HaveIBeenPwned entities are arrays of breach objects
  entities.forEach((e) => {
    const rows = Array.isArray(e?.data?.emailSearchResultHbp) ? e.data.emailSearchResultHbp : []
    rows.forEach((b: any) => b?.Name && push(out.breaches, b.Name))
  })

  // Rank names: a full "First Last" beats a bare surname, then corroboration.
  // Without the word-count weight the title became "lerner" rather than
  // "Efim Lerner", because a broker record repeats the surname alone.
  const nameScore = (n: string) => (n.trim().split(/\s+/).length >= 2 ? 100 : 0) + (nameHits.get(n) || 0)
  out.names.sort((a, b) => nameScore(b) - nameScore(a))

  return out
}

const CAP = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

/**
 * Build the header summary and the pivot list.
 * @param templateId which template is rendering, so wording can match
 */
export function buildSummary(templateId: string, apiData: any): Summary {
  const row: any = apiData || {}
  const h = harvest(apiData)
  const criteria = typeof row.criteria === 'string' && row.criteria.startsWith('data:image') ? 'uploaded image' : String(row.criteria ?? '')

  /* ---------------- title ---------------- */
  const title = h.names[0] && templateId !== 'geo' && templateId !== 'sentiment' ? h.names[0] : criteria || 'Search result'

  /* ---------------- answer line ---------------- */
  const answer: [string, string][] = []
  const criteriaIsPhone = /^\+?[\d\s()-]{7,}$/.test(criteria)
  if (templateId === 'person') {
    // "<Country> line · carrier" only makes sense for a phone lookup
    if (criteriaIsPhone && (h.carrier || h.country)) {
      answer.push([`${h.country ? CAP(h.country) : 'Mobile'} line`, [h.carrier, h.country].filter(Boolean).join(' · ')])
    }
    if (h.headline) answer.push([h.headline, ''])
    if (h.emails.length) answer.push([`${h.emails.length} e-mail${h.emails.length === 1 ? '' : 's'} found`, h.emails[0]])
    if (h.breaches.length) answer.push([`${h.breaches.length} breach${h.breaches.length === 1 ? '' : 'es'}`, h.breaches.slice(0, 2).join(', ')])
  }
  // No generic "just echo the criteria" fallback here anymore — an empty
  // `answer` now means "nothing person-specific was found," and ResultView.tsx
  // shows the workflow's own title in that case instead of duplicating the
  // criteria that's already the H1 above it.

  /* ---------------- pivots, from what was actually found ---------------- */
  const pivots: Pivot[] = []
  const add = (p: Pivot) => { if (!pivots.some((x) => x.title === p.title) && pivots.length < 5) pivots.push(p) }

  h.emails.slice(0, 2).forEach((e) => {
    add({ title: 'E-mail Lookup', sub: e, credits: 8, colour: C.email, type: 'combined_email', value: e })
    add({
      title: 'BreachScan · E-mail', sub: e, credits: 4, colour: C.breach,
      type: 'deepweb', value: e, sourceKind: 'email' as Source,
    })
  })
  h.phones.slice(0, 1).forEach((p) => {
    add({ title: 'Phone Lookup', sub: p, credits: 8, colour: C.phone, type: 'combined_phone', value: p })
    add({
      title: 'BreachScan · Phone', sub: p, credits: 4, colour: C.breach,
      type: 'deepweb', value: p, sourceKind: 'phone' as Source,
    })
  })
  h.socialIds.slice(0, 1).forEach((s) => add({
    title: 'BreachScan · Social ID', sub: s, credits: 4, colour: C.breach,
    type: 'deepweb', value: s, needsExtra: true, // which platform (vkid/instagramid/...) isn't known here
  }))
  if (h.images.length) {
    add({
      title: 'Face WebScan', sub: `${h.images.length} photo${h.images.length === 1 ? '' : 's'} found`, credits: 7, colour: C.face,
      type: 'face_search', value: '', needsExtra: true, // needs an uploaded image, not a URL
    })
  }
  if (h.names.length) add({ title: 'Name SocialScan', sub: h.names[0], credits: 4, colour: C.social, type: 'combined_name', value: h.names[0] })

  if (!pivots.length) {
    add({ title: 'Face WebScan', sub: 'confirm the identity', credits: 7, colour: C.face, type: 'face_search', value: '', needsExtra: true })
    add({ title: 'Name SocialScan', sub: 'once a name is known', credits: 4, colour: C.social, type: 'combined_name', value: '', needsExtra: true })
  }

  return { title, answer, pivots, photo: h.images[0], photos: h.images }
}
